import { useCallback, useEffect, useRef, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CloudUploadIcon,
  Delete01Icon,
  FileAttachmentIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { IconButton, IconButtonSize } from "shared/components/IconButton";
import { normalizeFile, normalizeFilename } from "shared/utils";
import { landXApi } from "api/landlord-surveys/land-x";
import type { LandXDocumentMeta } from "api/landlord-surveys/land-x.types";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPT = "image/*,application/pdf";

type UploadState =
  | { status: "idle" }
  | { status: "uploading"; progress: number; name: string; size: number }
  | {
      status: "done";
      url: string;
      name: string;
      size: number;
      contentType: string;
    }
  | { status: "error"; name: string };

const updateUploadProgress =
  (progress: number) =>
  (prev: UploadState): UploadState =>
    prev.status === "uploading" ? { ...prev, progress } : prev;

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const extractFileNameFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split("/");
    const last = segments[segments.length - 1];
    return decodeURIComponent(last) || "Document";
  } catch {
    return "Document";
  }
};

type Props = {
  value: string | undefined;
  token: string;
  onSelect: (value: string) => void;
  onUploadingChange: (uploading: boolean) => void;
  onDocumentReady: (doc: { url: string; metadata: LandXDocumentMeta }) => void;
};

export const FilePreview = ({
  value,
  token,
  onSelect,
  onUploadingChange,
  onDocumentReady
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  const [state, setState] = useState<UploadState>(() => {
    if (value)
      return {
        status: "done",
        url: value,
        name: extractFileNameFromUrl(value),
        size: 0,
        contentType: ""
      };
    return { status: "idle" };
  });

  useEffect(() => {
    onUploadingChange(state.status === "uploading");
  }, [state.status]);

  const uploadFile = useCallback(
    async (file: File) => {
      setState({
        status: "uploading",
        progress: 0,
        name: file.name,
        size: file.size
      });

      try {
        const presignedUrl = await landXApi.getPresignedUrl(token, file);
        const normalized = normalizeFile(file);

        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhrRef.current = xhr;

          xhr.upload.onprogress = (e) => {
            if (!e.lengthComputable) return;
            const progress = Math.round((e.loaded / e.total) * 100);
            setState(updateUploadProgress(progress));
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`Upload failed: ${xhr.status}`));
          };
          xhr.onerror = () => reject(new Error("Upload failed"));

          xhr.open("PUT", presignedUrl);
          xhr.setRequestHeader("Content-Type", normalized.type);
          xhr.send(normalized);
        });

        const s3Url = presignedUrl.split("?")[0];

        setState({
          status: "done",
          url: s3Url,
          name: file.name,
          size: file.size,
          contentType: file.type
        });

        onSelect(s3Url);

        const metadata: LandXDocumentMeta = {
          size: file.size,
          original_filename: normalizeFilename(file.name),
          content_type: file.type
        };
        onDocumentReady({ url: s3Url, metadata });
      } catch {
        setState({ status: "error", name: file.name });
      } finally {
        xhrRef.current = null;
      }
    },
    [token, onSelect, onDocumentReady]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > MAX_FILE_SIZE) {
        setState({ status: "error", name: file.name });
        return;
      }

      uploadFile(file);
      if (inputRef.current) inputRef.current.value = "";
    },
    [uploadFile]
  );

  const handleDelete = useCallback(() => {
    if (xhrRef.current) xhrRef.current.abort();
    setState({ status: "idle" });
    onSelect("");
  }, [onSelect]);

  if (state.status === "idle")
    return (
      <label className="h-[140px] gap-2 w-full flex justify-center items-center flex-col p-4 border rounded-md border-dashed border-primary-500 bg-primary-50 cursor-pointer hover:bg-primary-100 transition-colors">
        <HugeiconsIcon
          size={24}
          className="text-primary-500"
          icon={CloudUploadIcon}
        />
        <p className="text-sm font-bold text-primary-500">
          Upload file or picture
        </p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={handleFileChange}
        />
      </label>
    );

  if (state.status === "uploading")
    return (
      <div className="flex gap-3 rounded-lg border border-alpha-80 bg-white p-4">
        <div className="size-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-50">
          <HugeiconsIcon
            size={24}
            icon={FileAttachmentIcon}
            className="text-primary-500 shrink-0"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="truncate text-sm font-bold text-content-1000">
            {state.name}
          </p>
          <p className="text-xs text-content-200">
            {formatFileSize(state.size)} &middot; Uploading {state.progress}%
          </p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-alpha-80">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-200"
              style={{ width: `${state.progress}%` }}
            />
          </div>
        </div>
        <IconButton
          HugeIcon={Delete01Icon}
          className="text-danger-500 shrink-0 ml-auto"
          size={IconButtonSize.SM}
          onClick={handleDelete}
        />
      </div>
    );

  if (state.status === "done")
    return (
      <div className="flex gap-3 rounded-lg border border-alpha-80 bg-white p-4">
        <div className="size-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-50">
          <HugeiconsIcon
            size={24}
            icon={FileAttachmentIcon}
            className="text-primary-500 shrink-0"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="truncate text-sm font-bold text-content-1000">
            {state.name}
          </p>
          <p className="text-xs text-content-200">
            {state.size > 0 ? `${formatFileSize(state.size)} \u00b7 ` : ""}
            Uploaded
          </p>
        </div>
        <IconButton
          HugeIcon={Delete01Icon}
          className="text-danger-500 shrink-0 ml-auto"
          size={IconButtonSize.SM}
          onClick={handleDelete}
        />
      </div>
    );

  // error
  return (
    <div className="space-y-3">
      <div className="flex gap-3 rounded-lg border border-danger-200 bg-danger-50 p-4">
        <div className="size-10 flex items-center justify-center shrink-0 rounded-lg bg-danger-100">
          <HugeiconsIcon
            size={24}
            icon={FileAttachmentIcon}
            className="text-danger-500 shrink-0"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="truncate text-sm font-bold text-content-1000">
            {state.name}
          </p>
          <p className="text-xs text-danger-500">Upload failed</p>
        </div>
        <IconButton
          HugeIcon={Delete01Icon}
          className="text-danger-500 shrink-0 ml-auto"
          size={IconButtonSize.SM}
          onClick={handleDelete}
        />
      </div>
      <label className="h-[100px] gap-2 w-full flex justify-center items-center flex-col p-4 border rounded-md border-dashed border-primary-500 bg-primary-50 cursor-pointer hover:bg-primary-100 transition-colors">
        <HugeiconsIcon
          size={20}
          className="text-primary-500"
          icon={CloudUploadIcon}
        />
        <p className="text-sm font-bold text-primary-500">Try again</p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={handleFileChange}
        />
      </label>
    </div>
  );
};
